import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QuestionCard from '../components/questions/QuestionCard';
import GameProgressPanel from '../components/game/GameProgressPanel';
import GameSessionReviewPanel from '../components/game/GameSessionReviewPanel';
import { ArrowLeftIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { questionAPI, gameSessionAPI } from '../services/api';
import type { GameSessionReview } from '../services/api';
import FreeTierLimitModal from '../components/modals/FreeTierLimitModal';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { isFreeTierLimitError } from '../utils/freeTier';
import { QuestionApiModel } from '../types/questions';
import { getUserMessage } from '../utils/errors';

interface QuestionAttempt {
  question_number: number;
  is_correct: boolean;
  difficulty_rating: number | null;
  selected_answer: string;
  correct_answer: string;
}

const Survival: React.FC = () => {
  const [question, setQuestion] = useState<QuestionApiModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [answerStartTime, setAnswerStartTime] = useState<number>(Date.now());

  // Game state
  const [lives, setLives] = useState(3);
  const [streak, setStreak] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(1);
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);
  const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState<GameSessionReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [attemptHistory, setAttemptHistory] = useState<QuestionAttempt[]>([]);
  const [lastAnswerResult, setLastAnswerResult] = useState<{ isCorrect: boolean; selected: string; correctAnswer: string } | null>(null);
  const [showNextButton, setShowNextButton] = useState(false);
  const { subscriptionStatus, isLoading: isSubscriptionLoading, refresh: refreshSubscriptionStatus } = useSubscriptionStatus();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const hasStartedRef = useRef(false);

  const survivalUsage = subscriptionStatus?.free_tier_usage;
  const survivalLimitReached = useMemo(() => {
    if (!subscriptionStatus?.is_on_free_plan || !survivalUsage) {
      return false;
    }
    return survivalUsage.survival_sessions_used >= survivalUsage.survival_sessions_limit;
  }, [subscriptionStatus?.is_on_free_plan, survivalUsage]);
  const survivalLimitBlocking = useMemo(
    () => survivalLimitReached && !gameSessionId,
    [survivalLimitReached, gameSessionId]
  );
  const isPageLoading = (loading || isSubscriptionLoading) && !question && !error && !survivalLimitBlocking;
  const survivalUsageLabel = survivalUsage
    ? `${survivalUsage.survival_sessions_used}/${survivalUsage.survival_sessions_limit} runs`
    : null;
  const survivalResetLabel = useMemo(() => {
    if (!survivalUsage) return null;
    try {
      const resetDate = new Date(survivalUsage.resets_at);
      return `Resets ${resetDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    } catch {
      return `Resets ${survivalUsage.resets_on}`;
    }
  }, [survivalUsage]);

  const fetchNextQuestion = useCallback(async (questionNumber: number) => {
    try {
      setLoading(true);
      setError(null);
      setLastAnswerResult(null);
      setShowNextButton(false);

      const data = await questionAPI.getSurvivalQuestion(questionNumber);
      setQuestion(data);
      setAnswerStartTime(Date.now());
    } catch (err) {
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError(getUserMessage(err, 'Unable to load a Survival question. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  }, [refreshSubscriptionStatus]);

  const startNewGame = useCallback(async () => {
    if (survivalLimitBlocking) {
      setShowLimitModal(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const session = await gameSessionAPI.startSession('survival');
      setGameSessionId(session.id);

      setLives(3);
      setStreak(0);
      setCurrentQuestionNumber(1);
      setAttemptHistory([]);
      setLastAnswerResult(null);
      setShowNextButton(false);
      setGameOver(false);
      setCompletedSessionId(null);
      setReviewData(null);
      setReviewError(null);

      await refreshSubscriptionStatus().catch(() => {});
      await fetchNextQuestion(1);
    } catch (err) {
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError(getUserMessage(err, 'Failed to start game'));
      }
      setLoading(false);
    }
  }, [fetchNextQuestion, refreshSubscriptionStatus, survivalLimitBlocking]);

  useEffect(() => {
    if (isSubscriptionLoading || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    if (survivalLimitBlocking) {
      setShowLimitModal(true);
      setLoading(false);
      return;
    }

    startNewGame();
  }, [isSubscriptionLoading, startNewGame, survivalLimitBlocking]);

  useEffect(() => {
    if (gameOver && completedSessionId) {
      setReviewLoading(true);
      setReviewError(null);
      gameSessionAPI.getSessionReview(completedSessionId)
        .then((data) => {
          setReviewData(data);
        })
        .catch((err) => {
          console.error('Failed to load session review:', err);
          const message = getUserMessage(err, 'Failed to load review.');
          setReviewError(message);
        })
        .finally(() => setReviewLoading(false));
    }
  }, [gameOver, completedSessionId]);

  const handleAnswer = async (selectedAnswerValue: string, timeSpentSeconds?: number) => {
    if (!question || submittingAnswer || gameOver || !gameSessionId) return;

    setSubmittingAnswer(true);
    try {
      const resolvedTimeSpentSeconds =
        typeof timeSpentSeconds === 'number'
          ? timeSpentSeconds
          : Math.floor((Date.now() - answerStartTime) / 1000);

      const result = await questionAPI.submitAnswer({
        question_id: question.id,
        selected_answer: selectedAnswerValue,
        time_spent_seconds: resolvedTimeSpentSeconds,
        game_mode: 'survival',
        session_id: gameSessionId
      });

      if (result.question_progress) {
        setQuestion((prev) =>
          prev
            ? {
                ...prev,
                progress: result.question_progress,
              }
            : prev
        );
      }

      const isCorrect = result.is_correct;

      // Add to attempt history
      const attempt: QuestionAttempt = {
        question_number: currentQuestionNumber,
        is_correct: isCorrect,
        difficulty_rating: question.difficulty_rating,
        selected_answer: selectedAnswerValue,
        correct_answer: result.correct_answer
      };
      setAttemptHistory(prev => [...prev, attempt]);

      // Show immediate feedback
      setLastAnswerResult({
        isCorrect: isCorrect,
        selected: selectedAnswerValue,
        correctAnswer: result.correct_answer
      });

      // Update game state
      if (isCorrect) {
        const newStreak = streak + 1;
        setStreak(newStreak);

        // Update session progress
        await gameSessionAPI.updateProgress(gameSessionId, {
          questions_answered: currentQuestionNumber,
          correct_answers: newStreak,
          lives_remaining: lives
        });

        // Show next question button
        setShowNextButton(true);
      } else {
        const newLives = lives - 1;
        setLives(newLives);

        if (newLives <= 0) {
          const sessionId = gameSessionId;
          setGameOver(true);
          // Mark session as completed
          if (sessionId) {
            await gameSessionAPI.updateProgress(sessionId, {
              questions_answered: currentQuestionNumber,
              correct_answers: streak,
              lives_remaining: 0,
              is_completed: true
            });
            setCompletedSessionId(sessionId);
          }
          setGameSessionId(null);
        } else {
          // Update session progress and continue
          await gameSessionAPI.updateProgress(gameSessionId, {
            questions_answered: currentQuestionNumber,
            correct_answers: streak,
            lives_remaining: newLives
          });

          // Show next question button
          setShowNextButton(true);
        }
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        await refreshSubscriptionStatus().catch(() => {});
      } else {
        setError('Failed to submit answer. Please try again.');
      }
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleNextQuestion = async () => {
    if (gameOver) return;

    setShowNextButton(false);
    setCurrentQuestionNumber(currentQuestionNumber + 1);
    await fetchNextQuestion(currentQuestionNumber + 1);
  };

  const restartGame = () => {
    if (survivalLimitBlocking) {
      setShowLimitModal(true);
      return;
    }
    startNewGame();
  };

  const renderLives = () => {
    return Array.from({ length: 3 }, (_, i) => (
      <div key={i} className="inline-flex">
        {i < lives ? (
          <HeartSolidIcon className="h-6 w-6 text-red-500" />
        ) : (
          <HeartIcon className="h-6 w-6 text-gray-400" />
        )}
      </div>
    ));
  };

  if (gameOver) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Game Over!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Final Streak: <span className="font-bold text-subject-600">{streak}</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Great effort! Keep practicing to improve your streak.</p>
              <div className="space-y-3">
                <button
                  onClick={restartGame}
                  disabled={survivalLimitBlocking || loading}
                  className={`w-full px-4 py-2 rounded-lg transition-colors ${
                    survivalLimitBlocking || loading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-subject-600 text-white hover:bg-subject-500'
                  }`}
                >
                  Play Again
                </button>
                <Link
                  to="/dashboard"
                  className="block w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-center"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Review Your Run</h3>
            {reviewLoading && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-subject-500"></div>
                <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">Loading review...</span>
              </div>
            )}
            {!reviewLoading && reviewError && (
              <div className="py-4 text-sm text-red-600 dark:text-red-400">{reviewError}</div>
            )}
            {!reviewLoading && !reviewError && reviewData && (
              <GameSessionReviewPanel review={reviewData} />
            )}
            {!reviewLoading && !reviewError && !reviewData && (
              <p className="text-sm text-gray-600 dark:text-gray-400">No attempts recorded for this run.</p>
            )}
          </div>
        </div>
        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="survival"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-subject-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading question...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Error Loading Question</h2>
              <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
              <button
                onClick={() => fetchNextQuestion(currentQuestionNumber)}
                className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>

        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="survival"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {subscriptionStatus?.is_on_free_plan && survivalUsage && (
          <div
            className={`mb-6 rounded-lg border p-4 ${
              survivalLimitReached
                ? 'border-subject-300 bg-subject-50 text-subject-800 dark:border-subject-800/40 dark:bg-subject-900/20 dark:text-subject-200'
                : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/40 dark:bg-blue-900/20 dark:text-blue-200'
            }`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold">Free Plan Survival Access</p>
                <p className="text-sm">
                  {survivalLimitBlocking
                    ? 'You have used today’s free Survival run. Upgrade to keep pushing your streak.'
                    : 'You’re currently using your free Survival run for today. Upgrade for limitless attempts.'}
                </p>
              </div>
              <div className="text-sm sm:text-right">
                {survivalUsageLabel && (
                  <p className="font-semibold">{survivalUsageLabel}</p>
                )}
                {survivalResetLabel && (
                  <p className="text-xs opacity-80">{survivalResetLabel}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-subject-600 dark:hover:text-subject-400 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Dashboard
            </Link>
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Survival Mode
            </h1>
          </div>

          {/* Game Stats */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Question:</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {currentQuestionNumber}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Lives:</span>
              <div className="flex space-x-1">
                {renderLives()}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Streak:</span>
              <span className="text-lg font-bold text-subject-600 dark:text-subject-400">
                {streak}
              </span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        {question && (
          <div className="mb-8">
            <QuestionCard
              question={question}
              onAnswer={handleAnswer}
              isSubmitting={submittingAnswer}
              showAnswer={!!lastAnswerResult}
            />

            {/* Result Feedback */}
            {lastAnswerResult && (
              <div className={`mt-4 p-4 rounded-lg text-center ${
                lastAnswerResult.isCorrect
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <p className={`text-lg font-semibold mb-2 ${
                  lastAnswerResult.isCorrect
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {lastAnswerResult.isCorrect ? '🎉 Correct!' : '❌ Incorrect!'}
                </p>
                {!lastAnswerResult.isCorrect && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      You selected: <span className="font-semibold">{lastAnswerResult.selected}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Correct answer: <span className="font-semibold">{lastAnswerResult.correctAnswer}</span>
                    </p>
                    <p className="text-red-600 dark:text-red-300 mb-4">
                      You lost a life! Keep going!
                    </p>
                  </>
                )}

                {/* Next Question Button */}
                {showNextButton && !gameOver && (
                  <button
                    onClick={handleNextQuestion}
                    className="mt-4 px-6 py-3 bg-subject-600 text-white font-semibold rounded-lg hover:bg-subject-500 transition-colors focus:outline-none focus:ring-2 focus:ring-subject-500 focus:ring-offset-2"
                  >
                    Next Question →
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Progress Panel */}
        <GameProgressPanel
          attemptHistory={attemptHistory}
          currentQuestionNumber={currentQuestionNumber}
          gameMode="survival"
          lives={lives}
          maxLives={3}
        />

        {/* Game Instructions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Survival Mode Rules
          </h3>
          <ul className="space-y-2 text-gray-600 dark:text-gray-300">
            <li>• Answer questions correctly to build your streak</li>
            <li>• You have 3 lives - lose one for each wrong answer</li>
            <li>• Game ends when you run out of lives</li>
            <li>• Challenge yourself to get the highest streak possible!</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default Survival;
